import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { HiLocationMarker } from "react-icons/hi"
import { IoIosArrowForward, IoIosSettings } from "react-icons/io"
import { IoCloseOutline, IoSearch } from "react-icons/io5"
import { MdLocationPin } from "react-icons/md"
import { MdGpsFixed } from "react-icons/md"

import BottomSheet, {
  BottomSheetHandle,
} from "@fuse/sharedComponents/bottomSheet/BottomSheet"

import {
  getThePreciseLocation,
  usePredictionAutocomplete,
} from "@fuse/pages/explore/utility/locationFunctions"

export interface LocationBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

const LocationBottomSheet = forwardRef<
  LocationBottomSheetHandle,
  {
    onLocationSelected: (location: any) => void
    onOpenAdvancedSettings?: () => void
    value: any
    isDefaultBg?: boolean
  }
>(
  (
    { onLocationSelected, onOpenAdvancedSettings, value, isDefaultBg = false },
    ref,
  ) => {
    const bottomSheetRef = useRef<BottomSheetHandle>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [locationSearch, setLocationSearch] = useState<string>("")
    const { predictionList, fetchPlaceDetails } = usePredictionAutocomplete({
      locTerm: locationSearch,
      isSessionRequired: true,
    })
    const [sideBarLocalFilterState, setsideBarLocalFilterState] = useState({
      location: {
        lon: 0,
        lat: 0,
        nameOfPlace: "",
      },
      searchTerm: "",
      tags: [],
      changeTriggered: false,
    })

    const detailSetter = (data) => {
      setsideBarLocalFilterState((prev) => ({
        ...prev,
        ...data,
        changeTriggered: true,
      }))
    }

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      openModal: () => {
        bottomSheetRef.current?.openModal()
      },
      closeModal: () => {
        bottomSheetRef.current?.closeModal()
      },
    }))

    const list = [
      ...predictionList.map((item) => {
        return {
          icon: <HiLocationMarker color="#ffffff" size={20} />,
          title: item?.description,
          onClick: async () => {
            fetchPlaceDetails(item?.place_id, true)
              .then((res) => {
                detailSetter(res)
              })
              .catch((err) => {
                console.warn(err)
              })
            setLocationSearch("")

            // onLocationSelected(item)
          },
        }
      }),
    ]

    useEffect(() => {
      if (sideBarLocalFilterState.changeTriggered) {
        onLocationSelected(
          value &&
            value?.location.nameOfPlace &&
            !sideBarLocalFilterState.location.nameOfPlace
            ? value
            : sideBarLocalFilterState,
        )
      }
      bottomSheetRef.current?.closeModal()
    }, [sideBarLocalFilterState])

    const handleOpen = () => {
      // Small timeout to ensure the bottom sheet is rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }

    return (
      <BottomSheet
        ref={bottomSheetRef}
        title="Location"
        isDefaultBg={isDefaultBg}
        onOpen={handleOpen}
      >
        <div className="flex flex-col items-center justify-start gap-5 h-full">
          <input
            ref={inputRef}
            className="bg-transparent w-full focus:outline-none text-white ml-2 text-lg"
            value={locationSearch}
            onChange={(e) => {
              setLocationSearch(e.target.value)
            }}
            placeholder="Enter location"
            onBlur={() => {}}
          />

          {locationSearch.length === 0 && (
            <div
              className={`flex w-full gap-4 h-full`}
              onClick={() => {
                getThePreciseLocation(detailSetter, true)
              }}
            >
              <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white/10">
                <MdGpsFixed color="#ffffff" size={20} />
              </div>
              <div className="flex flex-col items-start">
                <div className={`text-[#fff] text-left text-base `}>
                  Detect the location
                </div>
                <div className="text-[#FFFFFF80] text-xs font-normal">
                  Using GPS
                </div>
              </div>
            </div>
          )}

          {locationSearch.length > 0 && (
            <div className="flex flex-col gap-4 w-full h-full overflow-y-auto">
              {list.map((item, _) => (
                <div
                  key={_}
                  className={`flex w-full gap-4 items-center ${list[_ + 1]?.title ? "" : ""}`}
                  onClick={() => {
                    item?.onClick && item?.onClick()
                  }}
                >
                  <div>
                    <div className="flex justify-center items-center w-9 h-9 rounded-full bg-white/10">
                      {item?.icon}
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className={`text-[#fff] text-left text-base `}>
                      {item?.title}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {onOpenAdvancedSettings && (
          <div
            className="flex flex-row items-center w-full gap-4 cursor-pointer"
            onClick={onOpenAdvancedSettings}
          >
            <IoIosSettings size={24} color="#ffffff20" />
            <p className="text-lg text-white/20 font-medium flex-1 text-left">
              Advanced settings
            </p>
            <IoIosArrowForward size={20} color="#ffffff20" />
          </div>
        )}
      </BottomSheet>
    )
  },
)

export default LocationBottomSheet
